import { api, rows } from './api';

/**
 * Every kind of record an id can point to. Used to render ids as names everywhere (tables, details)
 * and to pick records in generated forms.
 */
export interface EntityKind {
  kind: string;
  /** List endpoint (returns an array or a page). */
  listPath: string;
  /** Detail endpoint for a single id, when lists are too large to load whole. */
  getPath?: string;
  /** 'server' = list endpoint supports ?search=, otherwise the whole list is loaded and filtered locally. */
  search: 'server' | 'client';
  label(row: Record<string, unknown>): string;
  subtitle?(row: Record<string, unknown>): string | undefined;
  image?(row: Record<string, unknown>): string | undefined;
  /** Admin route that shows the record. */
  href?(id: string): string;
}

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const name = (r: Record<string, unknown>) => `${s(r.firstName)} ${s(r.lastName)}`.trim();
const photo = (r: Record<string, unknown>) => ((r.photo as { thumbUrl?: string; url?: string } | null)?.thumbUrl ?? (r.photo as { url?: string } | null)?.url) || undefined;

export const ENTITIES: Record<string, EntityKind> = {
  person: { kind: 'person', listPath: 'admin/people', getPath: 'admin/people/{id}', search: 'server', label: name, subtitle: (r) => s(r.phone || r.email) || undefined, image: photo, href: (id) => `/people/${id}` },
  horse: { kind: 'horse', listPath: 'admin/horses', getPath: 'admin/horses/{id}', search: 'server', label: (r) => s(r.name ?? (r.horse as Record<string, unknown>)?.name), subtitle: (r) => [r.breed, r.status].filter(Boolean).join(' · ') || undefined, image: photo, href: (id) => `/horses/${id}` },
  service: { kind: 'service', listPath: 'admin/services', search: 'client', label: (r) => s(r.nameEn), subtitle: (r) => s(r.type).replace(/_/g, ' ') },
  location: { kind: 'location', listPath: 'admin/locations', search: 'client', label: (r) => s(r.name) },
  resource: { kind: 'resource', listPath: 'admin/resources', search: 'client', label: (r) => s(r.name), subtitle: (r) => s(r.type) },
  stall: { kind: 'stall', listPath: 'admin/resources?type=stall', search: 'client', label: (r) => s(r.name) },
  arena: { kind: 'arena', listPath: 'admin/resources?type=arena', search: 'client', label: (r) => s(r.name) },
  membershipPlan: { kind: 'membershipPlan', listPath: 'admin/membership-plans', search: 'client', label: (r) => s(r.nameEn), subtitle: (r) => s(r.period) },
  boardingPlan: { kind: 'boardingPlan', listPath: 'admin/boarding-plans', search: 'client', label: (r) => s(r.name), subtitle: (r) => s(r.type) },
  boardingService: { kind: 'boardingService', listPath: 'admin/boarding-services', search: 'client', label: (r) => s(r.name) },
  boardingContract: { kind: 'boardingContract', listPath: 'admin/boarding-contracts', search: 'client', label: (r) => `${s((r.horse as Record<string, unknown>)?.name)} · ${s((r.plan as Record<string, unknown>)?.name)}` },
  package: { kind: 'package', listPath: 'admin/packages', search: 'client', label: (r) => s(r.nameEn), subtitle: (r) => `${s(r.credits)} credits` },
  offer: { kind: 'offer', listPath: 'admin/offers?limit=100', search: 'client', label: (r) => s(r.name), subtitle: (r) => s(r.code) || s(r.kind) },
  event: { kind: 'event', listPath: 'admin/events', search: 'client', label: (r) => s(r.titleEn), subtitle: (r) => s(r.status) },
  inventoryItem: { kind: 'inventoryItem', listPath: 'admin/inventory-items', search: 'client', label: (r) => s(r.name), subtitle: (r) => `${s(r.stockQty)} ${s(r.unit)}` },
  skillLevel: { kind: 'skillLevel', listPath: 'admin/skill-levels', search: 'client', label: (r) => `L${s(r.level)} · ${s(r.nameEn)}` },
  role: { kind: 'role', listPath: 'admin/roles', search: 'client', label: (r) => s(r.name), subtitle: (r) => s(r.key) },
  waiverTemplate: { kind: 'waiverTemplate', listPath: 'admin/waiver-templates', search: 'client', label: (r) => s(r.title), subtitle: (r) => `${s(r.kind)} · v${s(r.templateVersion)}` },
  taskTemplate: { kind: 'taskTemplate', listPath: 'admin/task-templates', search: 'client', label: (r) => s(r.title) },
  sessionTemplate: { kind: 'sessionTemplate', listPath: 'admin/session-templates', search: 'client', label: (r) => `${s(r.rrule)} ${s(r.startTime)}` },
  behaviourTag: { kind: 'behaviourTag', listPath: 'admin/behaviour-tags', search: 'client', label: (r) => s(r.labelEn), subtitle: (r) => s(r.severity) },
  occasionRule: { kind: 'occasionRule', listPath: 'admin/occasion-rules', search: 'client', label: (r) => s(r.name) },
  album: { kind: 'album', listPath: 'admin/albums?limit=100', search: 'client', label: (r) => s(r.titleEn) },
  booking: { kind: 'booking', listPath: 'admin/bookings', getPath: 'admin/bookings/{id}', search: 'client', label: (r) => s(r.reference), href: (id) => `/bookings?id=${id}` },
  order: { kind: 'order', listPath: 'admin/orders', getPath: 'admin/orders/{id}', search: 'client', label: (r) => `#${s(r.id).slice(0, 8)}`, href: (id) => `/orders?id=${id}` },
  session: { kind: 'session', listPath: 'admin/sessions', getPath: 'admin/sessions/{id}', search: 'client', label: (r) => s(((r.session as Record<string, unknown>) ?? r).startsAt), href: (id) => `/schedule?session=${id}` },
};

/**
 * Which entity an id field refers to, from its name and the screen it is on
 * (`serviceId` on a boarding request is a boarding service, not a bookable service).
 */
export function entityKindOf(key: string, context = ''): EntityKind | null {
  const k = key.replace(/Ids?$/, '');
  if (!/Ids?$/.test(key) || key === 'id' || key === 'tenantId') return null;
  if (/MediaId$/.test(key) || /mediaIds?$/i.test(key)) return null;
  const boarding = /boarding|service-requests/.test(context);
  if (/person$/i.test(k) || ['holder', 'referrer', 'friend', 'guardian', 'minor'].includes(k)) return ENTITIES.person;
  switch (k) {
    case 'horse':
      return ENTITIES.horse;
    case 'service':
      return boarding ? ENTITIES.boardingService : ENTITIES.service;
    case 'location':
      return ENTITIES.location;
    case 'resource':
      return ENTITIES.arena;
    case 'stallResource':
      return ENTITIES.stall;
    case 'plan':
    case 'pendingPlan':
      return boarding ? ENTITIES.boardingPlan : ENTITIES.membershipPlan;
    case 'package':
      return ENTITIES.package;
    case 'offer':
      return ENTITIES.offer;
    case 'event':
      return ENTITIES.event;
    case 'item':
      return ENTITIES.inventoryItem;
    case 'level':
      return ENTITIES.skillLevel;
    case 'role':
      return ENTITIES.role;
    case 'waiverTemplate':
      return ENTITIES.waiverTemplate;
    case 'template':
      return /task/.test(context) ? ENTITIES.taskTemplate : /session|schedule/.test(context) ? ENTITIES.sessionTemplate : /waiver/.test(context) ? ENTITIES.waiverTemplate : null;
    case 'behaviourTag':
      return ENTITIES.behaviourTag;
    case 'rule':
      return ENTITIES.occasionRule;
    case 'album':
      return ENTITIES.album;
    case 'contract':
      return ENTITIES.boardingContract;
    case 'booking':
    case 'acceptedBooking':
      return ENTITIES.booking;
    case 'order':
    case 'invoicedOrder':
      return ENTITIES.order;
    case 'session':
      return ENTITIES.session;
    default:
      return null;
  }
}

/** Whole list for client-searched kinds (cached by react-query under ['entity-list', kind]). */
export async function loadEntityList(kind: EntityKind, search?: string): Promise<Array<Record<string, unknown>>> {
  if (kind.search === 'server') {
    const [path, qs] = kind.listPath.split('?');
    const query = Object.fromEntries(new URLSearchParams(qs ?? ''));
    return rows<Record<string, unknown>>(await api.get(path, { query: { ...query, search: search || undefined, limit: 20 } }));
  }
  const [path, qs] = kind.listPath.split('?');
  return rows<Record<string, unknown>>(await api.get(path, { query: Object.fromEntries(new URLSearchParams(qs ?? '')) }));
}

export async function loadEntity(kind: EntityKind, id: string): Promise<Record<string, unknown> | null> {
  if (kind.getPath) {
    const row = await api.get<Record<string, unknown>>(kind.getPath.replace('{id}', id));
    // Some detail endpoints wrap the record (e.g. horses return a card { horse, … }).
    return (row?.horse as Record<string, unknown>) && kind.kind === 'horse' ? { ...(row.horse as Record<string, unknown>), photo: row.photo } : row;
  }
  return (await loadEntityList(kind)).find((r) => r.id === id) ?? null;
}
